import React, { useState, useMemo } from 'react';

import styled from 'styled-components';

import { getModel } from '@hooks/API/Contact/UseContactsList';
import { useProject, forceReloadProject } from '@hooks/API/Project/UseProject';

import ModelDrawer from '@components/ContactDetails/ModelDetails/Drawer/ModelDrawer';
import { NwAddButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import { ModelCover } from '@components/ModelCover';
import PrevisionalModelForm from '@components/Project/PrevisionalModels/PrevisionalModelForm';
import ReportCreationDrawer from '@components/Project/ProjectReport/ReportCreationDrawer';
import { isJobEventActive, isExtEventActive } from '@components/Project/ProjectUtils';
import AgendaDrawer from '@components/Schedule/Agenda/AgendaDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ListWrapper = styled.div`
    .model-group {
        &:not(:last-child) {
            margin-bottom: 0.5rem;
        }
    }

    .group-header {
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: 600;
        color: ${(props) => props.theme.ColorTextSecondary};
        text-transform: uppercase;
        padding: 1rem 0 0.5rem 0;
        margin-top: 1rem;
        letter-spacing: 0.5px;

        &.first {
            margin-top: 0;
        }
    }

    .models-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
        gap: 0.5rem 1rem;
        align-items: start;
        padding-left: 2rem;
    }

    .model-item {
        display: flex;
        gap: 0.5rem;
        min-height: 5.8rem;

        .model-avatar {
            cursor: pointer;
            flex-shrink: 0;
        }

        .model-content {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .model-info {
            .model-name {
                font-size: ${(props) => props.theme.FontSizeBase};
                font-weight: 600;
                text-transform: capitalize;
                line-height: 1.2;
                margin-bottom: 0.25rem;
                cursor: pointer;
            }

            .model-agency {
                font-size: ${(props) => props.theme.FontSizeSm};
                color: ${(props) => props.theme.TextColorMuted};
                line-height: 1.1em;
            }
        }

        .model-actions {
            .pdf-icon {
                cursor: pointer;
                font-size: 1.5rem;
                color: #000;
            }
        }

        &.pending .model-name,
        &.other-events .model-name,
        &.cancelled .model-name {
            font-weight: 400;
        }

        &.cancelled {
            opacity: 0.5;
        }
    }
`;

const ModelItem = ({ model, description, showAgenda, showPdfIcon, projectId, status }) => {
    const [isModelViewDrawer, setIsModelViewDrawer] = useState(false);
    const [isReportDrawer, setIsReportDrawer] = useState(false);

    const handleViewModel = () => {
        setIsModelViewDrawer(true);
    };

    const handleCloseModelDrawer = () => {
        setIsModelViewDrawer(false);
    };

    const handleOpenReport = (e) => {
        e.stopPropagation();
        setIsReportDrawer(true);
    };

    const handleCloseReportDrawer = () => {
        setIsReportDrawer(false);
    };

    return (
        <>
            <div className={`model-item${status ? ` ${status}` : ''}`}>
                <div className="model-avatar" onClick={handleViewModel}>
                    <ModelCover model={model} classN="model-cover-middle" />
                </div>
                <div className="model-content">
                    <div className="model-info">
                        <div className="model-name" onClick={handleViewModel}>
                            {model.N} {model.S}
                        </div>
                        <div className="model-agency">
                            {model.archived ? 'Archived' : description ? description : model.MAN}
                        </div>
                    </div>
                    {showPdfIcon && !model.archived && (
                        <div className="model-actions">
                            <NwIcon
                                className="pdf-icon"
                                icon={byPrefixAndName.fasl['file-pdf']}
                                onClick={handleOpenReport}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModelViewDrawer && (
                <>
                    {showAgenda ? (
                        <AgendaDrawer modelId={model.ID} onClose={handleCloseModelDrawer} />
                    ) : (
                        <ModelDrawer modelID={model.ID} onClose={handleCloseModelDrawer} />
                    )}
                </>
            )}
            {isReportDrawer && (
                <ReportCreationDrawer projectId={projectId} modelId={model.ID} onClose={handleCloseReportDrawer} />
            )}
        </>
    );
};

const ProjectModels = ({ projectId }) => {
    const { data: currentProject } = useProject(projectId);
    const [touchForm, setTouchForm] = useState(false);
    const [isAddModel, setIsAddModel] = useState(false);

    const handleAddProjectModel = () => {
        setIsAddModel(true);
    };

    const handleCloseEdit = () => {
        setIsAddModel(false);
    };

    const handleAfterUpdated = () => {
        forceReloadProject(projectId);
        handleCloseEdit();
    };

    const groupedModels = useMemo(() => {
        if (!currentProject || !currentProject.ProjectModels || !currentProject.Events)
            return { confirmed: [], pending: [], otherEvents: [], cancelled: [] };

        // Build models with confirmation properties
        const modelsWithConfirmation = currentProject.ProjectModels.map((projectModel) => {
            const modelData = getModel(projectModel.ModelID);
            if (!modelData) return null;

            // Find all event dates for this model
            const modelEventDates = [];
            for (const event of currentProject.Events) {
                for (const eventDate of event.EventDates) {
                    const eventDateModel = eventDate.EventDatesModels?.find(
                        (edm) => edm.ModelID === projectModel.ModelID
                    );
                    if (eventDateModel) {
                        modelEventDates.push({
                            EventType: event.TypeName,
                            OptionNumber: eventDateModel.OptionNumber,
                            Cancel: eventDateModel.Cancel,
                        });
                    }
                }
            }

            // Calculate confirmation properties
            const activeJobs = modelEventDates.filter(
                (eventDate) => isJobEventActive(eventDate) || isExtEventActive(eventDate)
            );
            const hasConfirmation = activeJobs.length > 0;
            const alwaysCancelled =
                modelEventDates.length > 0 && modelEventDates.every((eventDate) => eventDate.Cancel);

            return {
                ...modelData,
                HasConfirmation: hasConfirmation,
                AlwaysCancelled: alwaysCancelled,
                HasNoEvents: modelEventDates.length === 0,
            };
        }).filter((model) => model !== null);

        // Group models by priority
        const confirmed = [];
        const pending = [];
        const otherEvents = [];
        const cancelled = [];

        modelsWithConfirmation.forEach((model) => {
            if (model.HasConfirmation) {
                confirmed.push(model);
            } else if (!model.AlwaysCancelled && !model.HasNoEvents) {
                pending.push(model);
            } else if (model.HasNoEvents) {
                otherEvents.push(model);
            } else {
                cancelled.push(model);
            }
        });

        // Sort each group alphabetically
        const sortByName = (a, b) => a.N.localeCompare(b.N) || a.S.localeCompare(b.S);
        confirmed.sort(sortByName);
        pending.sort(sortByName);
        otherEvents.sort(sortByName);
        cancelled.sort(sortByName);

        return { confirmed, pending, otherEvents, cancelled };
    }, [currentProject?.ProjectModels, currentProject?.Events]);

    const totalModels =
        groupedModels.confirmed.length +
        groupedModels.pending.length +
        groupedModels.otherEvents.length +
        groupedModels.cancelled.length;

    return (
        <div className="internal-card">
            {currentProject && totalModels > 0 && (
                <>
                    <div className="header">
                        <div className="title-container">
                            <NwIcon className="icon" icon={byPrefixAndName.fasl['user']} />
                            <div className="title">Models</div>
                        </div>
                        <NwAddButton ghost onClick={handleAddProjectModel} />
                    </div>
                    <div className="content">
                        <ListWrapper>
                            {groupedModels.confirmed.length > 0 && (
                                <div className="model-group">
                                    <div className="group-header first">Confirmed</div>
                                    <div className="models-grid">
                                        {groupedModels.confirmed.map((model) => (
                                            <ModelItem key={model.ID} model={model} showPdfIcon projectId={projectId} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {groupedModels.pending.length > 0 && (
                                <div className="model-group">
                                    <div
                                        className={`group-header${groupedModels.confirmed.length === 0 ? ' first' : ''}`}
                                    >
                                        Pending Options
                                    </div>
                                    <div className="models-grid">
                                        {groupedModels.pending.map((model) => (
                                            <ModelItem key={model.ID} model={model} status="pending" />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {groupedModels.otherEvents.length > 0 && (
                                <div className="model-group">
                                    <div
                                        className={`group-header${groupedModels.confirmed.length === 0 && groupedModels.pending.length === 0 ? ' first' : ''}`}
                                    >
                                        Castings or other events
                                    </div>
                                    <div className="models-grid">
                                        {groupedModels.otherEvents.map((model) => (
                                            <ModelItem key={model.ID} model={model} status="other-events" />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {groupedModels.cancelled.length > 0 && (
                                <div className="model-group">
                                    <div
                                        className={`group-header${groupedModels.confirmed.length === 0 && groupedModels.pending.length === 0 && groupedModels.otherEvents.length === 0 ? ' first' : ''}`}
                                    >
                                        Cancelled
                                    </div>
                                    <div className="models-grid">
                                        {groupedModels.cancelled.map((model) => (
                                            <ModelItem key={model.ID} model={model} status="cancelled" />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </ListWrapper>
                    </div>
                    {isAddModel && (
                        <NwDrawer
                            touchForm={touchForm}
                            onTouchForm={setTouchForm}
                            title={'Add Project Model'}
                            onClose={handleCloseEdit}
                        >
                            <PrevisionalModelForm
                                PrevisionalTransactions={currentProject.PrevisionalTransactions}
                                projectId={projectId}
                                onCancel={handleCloseEdit}
                                onAfterUpdated={handleAfterUpdated}
                                onTouchForm={setTouchForm}
                            />
                        </NwDrawer>
                    )}
                </>
            )}
        </div>
    );
};

export default ProjectModels;
